import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ErrorState, LoadingState } from "@/components/States";
import { flowsApi } from "@/lib/api";
import { formatDateTime, shortId } from "@/lib/format";
import { ScanType, type MaterialItemRead, type ScanType as ScanTypeT } from "@/lib/types";

export default function Traceability() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const id = params.get("id") || "";

  const trace = useQuery({
    queryKey: ["flow", "trace", id],
    queryFn: () => flowsApi.trace(id),
    enabled: !!id,
  });

  return (
    <>
      <PageHeader
        eyebrow="traceability"
        title={t("traceability.title")}
        description={t("traceability.description")}
      />

      <section className="card p-5 space-y-4">
        <ItemFinder
          onResolved={(item) => setParams({ id: item.id }, { replace: true })}
          initialId={id}
        />
      </section>

      {!id ? null : trace.isLoading ? (
        <LoadingState />
      ) : trace.isError ? (
        <ErrorState error={trace.error} />
      ) : (
        <section className="space-y-4">
          <TraceCard
            heading={t("traceability.parents", { count: trace.data!.parents.length })}
            items={trace.data!.parents}
            emptyText={t("traceability.noParents")}
          />
          <div className="card p-5 border-accent">
            <h2 className="text-base text-text-heading">{t("traceability.currentItem")}</h2>
            <ItemDetails item={trace.data!.item} />
          </div>
          <TraceCard
            heading={t("traceability.children", { count: trace.data!.children.length })}
            items={trace.data!.children}
            emptyText={t("traceability.noChildren")}
          />
        </section>
      )}
    </>
  );
}

function ItemFinder({
  onResolved,
  initialId,
}: {
  onResolved: (item: MaterialItemRead) => void;
  initialId: string;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"id" | "scan">("id");
  const [idInput, setIdInput] = useState(initialId);
  const [scanType, setScanType] = useState<ScanTypeT>("RFID");
  const [tag, setTag] = useState("");

  const byId = useMutation({
    mutationFn: (val: string) => flowsApi.get(val),
    onSuccess: (item) => onResolved(item),
  });
  const byScan = useMutation({
    mutationFn: () => flowsApi.lookup({ scan_type: scanType, tag_value: tag }),
    onSuccess: (item) => onResolved(item),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
        <button className={`btn ${mode === "id" ? "btn-primary" : ""}`} onClick={() => setMode("id")}>{t("traceability.byId")}</button>
        <button className={`btn ${mode === "scan" ? "btn-primary" : ""}`} onClick={() => setMode("scan")}>{t("traceability.byScan")}</button>
      </div>

      {mode === "id" ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (idInput) byId.mutate(idInput); }}
        >
          <input
            type="text"
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            placeholder="UUID material item"
            className="flex-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
          />
          <button type="submit" className="btn btn-primary" disabled={byId.isPending}>{t("traceability.find")}</button>
          {byId.isError && <span className="text-danger text-sm self-center">{byId.error instanceof Error ? byId.error.message : t("traceability.notFound")}</span>}
        </form>
      ) : (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => { e.preventDefault(); if (tag) byScan.mutate(); }}
        >
          <select
            value={scanType}
            onChange={(e) => setScanType(e.target.value as ScanTypeT)}
            className="px-3 py-2 rounded-md bg-surface border border-border text-sm"
          >
            {ScanType.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="tag value"
            className="flex-1 min-w-[200px] px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
          />
          <button type="submit" className="btn btn-primary" disabled={byScan.isPending}>{t("traceability.find")}</button>
          {byScan.isError && <span className="text-danger text-sm self-center">{byScan.error instanceof Error ? byScan.error.message : t("traceability.notFound")}</span>}
        </form>
      )}
    </div>
  );
}

function TraceCard({
  heading,
  items,
  emptyText,
}: {
  heading: string;
  items: MaterialItemRead[];
  emptyText: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="card p-5">
      <h2 className="text-base text-text-heading">{heading}</h2>
      {items.length === 0 ? (
        <p className="text-text-muted text-sm mt-2">{emptyText}</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
              <div className="flex items-center gap-3">
                <span className="font-mono text-accent-strong">{i.code}</span>
                <span className="text-text-secondary text-sm">{i.name_ru ?? ""}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-text-muted">
                {i.rfid_tag && <span className="font-mono">{i.rfid_tag}</span>}
                <StatusPill kind="flow" value={i.status} />
                <Link to={`/traceability?id=${i.id}`} className="text-accent-strong hover:underline">{t("traceability.open")}</Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemDetails({ item }: { item: MaterialItemRead }) {
  const { t } = useTranslation();
  return (
    <dl className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
      <Field label={t("traceability.fCode")} value={<span className="font-mono">{item.code}</span>} />
      <Field label={t("traceability.fType")} value={<span className="pill">{item.flow_type}</span>} />
      <Field label={t("traceability.fStatus")} value={<StatusPill kind="flow" value={item.status} />} />
      <Field label={t("traceability.fStage")} value={item.stage_id !== null ? `STAGE_${item.stage_id}` : "—"} mono />
      <Field label="RFID" value={item.rfid_tag ?? "—"} mono />
      <Field label={t("traceability.fBarcode")} value={item.barcode ?? "—"} mono />
      <Field label="QR" value={item.qr_code ?? "—"} mono />
      <Field label="Batch" value={item.batch_id ? shortId(item.batch_id) : "—"} mono />
      <Field label={t("traceability.fSource")} value={item.source ?? "—"} mono />
      <Field label={t("traceability.fCreated")} value={formatDateTime(item.created_at)} mono />
      {item.metadata && Object.keys(item.metadata).length > 0 && (
        <div className="col-span-full">
          <p className="text-xs uppercase tracking-wider font-mono text-text-muted">Metadata</p>
          <pre className="mt-1 p-3 bg-surface-alt rounded text-xs font-mono overflow-x-auto">
            {JSON.stringify(item.metadata, null, 2)}
          </pre>
        </div>
      )}
    </dl>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{label}</p>
      <p className={`mt-1 ${mono ? "font-mono text-xs" : ""}`}>{value}</p>
    </div>
  );
}
